import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import checkIcon from '../../../../Assets/icons/check.svg';
import arrowBackIcon from '../../../../Assets/icons/arrow_back.svg';
import StepFrame from '../../Scenes/Checkout/StepFrame';
import ChoiceButton from '../../Scenes/Checkout/ChoiceButton';
import Modal, { MODAL_TRANSITION_MS } from '../Modal';

/*
 * Entrance plays as a plain keyframe animation, unconditionally, the instant
 * the node mounts, rather than relying on CSSTransition's enter/enter-active
 * class swap. Exit stays CSSTransition-driven: the exit-active/exit-done
 * classes turn the animation off and hand over to `transition` for the way
 * out. Same pattern as PromoCodeModal.
 */
/** The card: scales and fades in, and back out. StepFrame provides its own padding. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  border-radius: 36px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposClearCartCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-clear-cart-exit-active &,
  .epos-clear-cart-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposClearCartCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

interface Props {
  /** Shown while "Start over" is waiting on confirmation that the cart can be emptied. */
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Guards against silently carrying the current order's cart into "Start
 * over". EposFlow only opens this when the cart still holds items - an empty
 * cart starts over right away, no confirmation needed.
 */
const ClearCartConfirm: FC<Props> = ({ open, onConfirm, onCancel }) => {
  const { i18n } = useI18n();

  return (
    <Modal
      open={open}
      exitPrefix="epos-clear-cart"
      onBackdropTap={onCancel}
      testId="epos-clear-cart-confirm">
      <Card>
        <StepFrame
          testId="epos-clear-cart-step"
          title={i18n('Express.StartOver.ClearCart.Title')}
          subtitle={i18n('Express.StartOver.ClearCart.SubTitle')}
          actions={
            <>
              <ChoiceButton
                label={i18n('Express.StartOver.ClearCart.Cancel')}
                icon={arrowBackIcon}
                onClick={onCancel}
                testId="epos-clear-cart-cancel"
              />
              <ChoiceButton
                primary
                label={i18n('Express.StartOver.ClearCart.Confirm')}
                icon={checkIcon}
                onClick={onConfirm}
                testId="epos-clear-cart-confirm-button"
              />
            </>
          }
        />
      </Card>
    </Modal>
  );
};

export default memo(ClearCartConfirm);
